/* eslint-disable react-hooks/exhaustive-deps */
import {
    SegmentedCarousel,
    useSegmentedCarouselController,
} from '@eventbrite/collection-carousel';
import {
    PopularEventsExperiment,
    TrendingSearchValues,
    useGetStatsigEnrollment,
} from '@eventbrite/discover-utils';
import { FormattedEvent } from '@eventbrite/event-renderer';
import { useIsMobileDevice } from '@eventbrite/feature-detection';
import { LoadingSkeleton } from '@eventbrite/loading-skeleton';
import { Typography } from '@eventbrite/marmalade';
import { searchOrganicEvents } from '@eventbrite/search-utils';
import { useQuery } from '@tanstack/react-query';
import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import RankedCardContainer from '../../../../containers/RankedCardContainer/RankedCardContainer';
import { ConnectedProps, DiscoverState } from '../../../../types/index';
import { transformResponse } from '../../../categoryBrowse/hooks/queries/events';
import styles from './PopularEventsCarousel.module.scss';
import { DeviceLocationState, getLocation, getQuery, getTitle } from './utils';

export const DisconnectedPopularEventsCarousel = (
    props: PopularEventsCarouselProps,
) => {
    const { locale, location } = props;
    const isMobile = useIsMobileDevice(1219);
    const controllerProps = useSegmentedCarouselController();
    const [deviceLocation, setDeviceLocation] = useState<
        DeviceLocationState | any
    >(undefined);
    const headerText = getTitle(
        location?.currentPlace,
        deviceLocation?.currentPlace,
    );

    const enableExperiment = !!(
        deviceLocation && location?.currentPlace != deviceLocation?.currentPlace
    );
    const PopularEventsExperimentVariant = useGetStatsigEnrollment({
        name: PopularEventsExperiment.name,
        paramName: PopularEventsExperiment.paramName,
        defaultValue: PopularEventsExperiment.values.test,
        enabled: enableExperiment,
    });
    const experimentLoading =
        PopularEventsExperimentVariant.status == 'loading';
    const showVariant =
        PopularEventsExperimentVariant.variant == TrendingSearchValues.test;
    const enableQuery =
        PopularEventsExperimentVariant.status != 'disabled' ||
        deviceLocation == location;

    useEffect(() => {
        getLocation(setDeviceLocation, location);
    }, []);

    useEffect(() => {
        if (deviceLocation) setDeviceLocation(location);
    }, [location, showVariant]);

    const eventData = useQuery(
        ['homepage-trending-events', deviceLocation],
        () => searchOrganicEvents(getQuery(deviceLocation)),
        {
            select: transformResponse,
            refetchOnMount: false,
            enabled: enableQuery,
            staleTime: 300000, //5 minutes: 5 * 60 * 1000
        },
    );

    const showCarousel =
        eventData.isLoading ||
        experimentLoading ||
        (eventData.data && eventData.data.events.length > 0);

    return (
        <div className={styles.root}>
            {showCarousel && (
                <div className={styles.carouselContainer}>
                    <SegmentedCarousel
                        {...controllerProps}
                        hideOverflow={!isMobile}
                        rootPaddingLeft={0}
                        rootPaddingRight={isMobile ? undefined : 20}
                    >
                        <SegmentedCarousel.Header>
                            {eventData.isLoading || experimentLoading || (
                                <Typography
                                    className={styles.carouselHeader}
                                    variant="heading-sm"
                                    color="black"
                                >
                                    {headerText}
                                </Typography>
                            )}
                        </SegmentedCarousel.Header>
                        <CarouselTrack
                            carouselLoading={eventData.isLoading}
                            locale={locale}
                            popularEvents={
                                eventData.data ? eventData.data.events : []
                            }
                        />
                        <SegmentedCarousel.Footer />
                    </SegmentedCarousel>
                </div>
            )}
        </div>
    );
};

const _mapStateToProps = ({ app, location }: DiscoverState) => {
    return {
        locale: app.locale,
        location: location,
    };
};

type ReduxProps = ConnectedProps<typeof _mapStateToProps>;
type PopularEventsCarouselProps = ReduxProps;

export default connect(_mapStateToProps)(DisconnectedPopularEventsCarousel);

interface CarouselTrackProps {
    carouselLoading: boolean;
    locale: string;
    popularEvents: FormattedEvent[];
}
const CarouselTrack = (props: CarouselTrackProps) => {
    const { carouselLoading, locale, popularEvents } = props;
    return (
        <SegmentedCarousel.Track
            key={`trending-events-isLoading-${carouselLoading}`}
        >
            {carouselLoading
                ? Array.from({ length: 8 }, (_, i) => (
                      <LoadingSkeleton height={320} key={i} />
                  ))
                : popularEvents.map((event, i) => (
                      <RankedCardContainer
                          event={event}
                          key={i}
                          locale={locale}
                          rank={i + 1}
                      />
                  ))}
        </SegmentedCarousel.Track>
    );
};
